import React, { useState, useRef } from "react";
import { CSVLink } from "react-csv";
import { DownloadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import {
  // DateFormatter,
  FormatDateForReport,
} from "../../../../Components/CommonUI/DateFormatter";
import ConfirmPopup from "../../../../Components/ConfirmPopup/ConfirmPopup";
import { useSelector } from "react-redux";

// Amount formatting (ensuring "0.00" format)
const formatAmount = (value: any) => {
  const num = parseFloat(value);
  return !isNaN(num) ? `${num.toFixed(2)}` : 0.0;
};

// Date formatting (keeping only YYYY-MM-DD)
const formatDate = (dateString: string) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  // Format the date to "M/D/YYYY"
  return new Intl.DateTimeFormat("en-US").format(date);
};

// Function to sanitize and format data for CSV
const sanitizeCsvData = (data: any[]) => {
  return data.map((row) => ({
    id: row.id, // If you have an ID field
    documentUrl: row.documentUrl, // If you have a document URL
    documentOwner: row.documentOwner, // Document Owner
    type: row.type, // Document Type
    invoiceDate: FormatDateForReport(
      row.invoiceDate,
      "DD MMM YYYY",
      "DD MMM YYYY",
    ), // Invoice Date
    supplier: row.supplier, // Supplier
    extractedPoNumber: row.extractedPoNumber, // PO Number
    documentReference: row.documentReference, // Document Reference
    dueDate: FormatDateForReport(row.dueDate, "DD MMM YYYY", "DD MMM YYYY"), // Due Date
    category: row.category, // Category
    productOrService: row.productOrService, // Product or Service
    markAsRebillable: row.markAsRebillable, // Mark as Rebillable
    description: row.description, // Description
    // amount: formatAmount(row.amount),
    currency: row.currency, // Currency
    discountAmount: formatAmount(row.discountAmount), // Discount Amount
    totalAmount: formatAmount(row.totalAmount), // Total Amount
    taxAmount: formatAmount(row.taxAmount), // Tax Amount
    netAmount: formatAmount(row.netAmount), // Net Amount
    payment: formatAmount(row.payment), // Payment status
    paid: row.paid, // Paid status
    paymentMethod: row.paymentMethod, // Payment Method
    lineItems: row.lineItems, // Line Items
  }));
};

interface CsvDownloadProps {
  csvData: any[];
  exportToCSV: (includeDetails: boolean) => void;
  exportDocHeaders?: any[];
}

const CsvDownload = ({
  csvData,
  exportToCSV,
  exportDocHeaders,
  // includeDetails,
}: CsvDownloadProps) => {
  // FIX: useSelector must be inside component
  const selectedClient = useSelector((state: any) => state.selectedClient);

  const [openPopup, setOpenPopup] = useState(false);
  const [includeDetails, setIncludeDetails] = useState(false);

  // FIX: moved inside component because selectedClient is used here
  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const year = today.getFullYear();
  const formattedDate = `${day}-${month}-${year}`;

  const clientName = selectedClient?.selectedClient?.name || "unknown-client";

  const safeClientName = clientName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-");

  const fileName = `${safeClientName}-${formattedDate}.csv`;

  const csvSanitizedData = sanitizeCsvData(csvData);

  const csvLinkRef = useRef<any>(null);

  // Handle the export when user confirms
  const handleExportConfirm = () => {
    if (csvLinkRef.current) {
      csvLinkRef.current.link.click(); // trigger CSV download
    }
    exportToCSV(includeDetails);
    setOpenPopup(false);
    setIncludeDetails(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setOpenPopup(false);
    setIncludeDetails(false);
  };

  return (
    <>
      {/* <CSVLink
        data={csvSanitizedData}
        headers={exportDocHeaders}
        filename={fileName}
        ref={csvLinkRef}
        style={{ display: "none" }}
      /> */}

      {/* Confirmation Popup */}
      <ConfirmPopup
        isOpen={openPopup}
        title="Export Invoices as CSV"
        message={
          <>
            <span
              style={{
                fontWeight: "bold",
                display: "block",
                marginBottom: "15px",
              }}
            >
              Export your Invoices as a CSV file.
            </span>

            {/* Optional checkbox */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "15px",
                cursor: "pointer",
                fontWeight: "normal",
                fontSize: "var(--font-size-base)",
              }}
            >
              <input
                type="checkbox"
                checked={includeDetails}
                onChange={(e) => setIncludeDetails(e.target.checked)}
                style={{ width: "16px", height: "16px", cursor: "pointer" }}
              />
              Archive items after export
            </label>
          </>
        }
        confirmLabel="Export"
        cancelLabel="Cancel"
        confirmColor="var(--color-brand)"
        onConfirm={handleExportConfirm}
        onCancel={handleCancel}
      />

      {/* Export Button */}
      <Button
        disabled={csvData.length === 0}
        onClick={() => setOpenPopup(true)}
        type="primary"
        icon={<DownloadOutlined />}
        style={{
          marginRight: 10,
          backgroundColor: csvData.length === 0 ? "#787878ff" : "var(--color-brand)",
          color: "white",
        }}
      >
        Export CSV
      </Button>
    </>
  );
};

export default CsvDownload;
